/**
 * Subscription Free Product
 *
 * Automatically adds a configured free product to the cart whenever at least
 * one subscription item (selling_plan_allocation) is present.
 * Removes it the moment all subscription items are gone.
 *
 * Config is injected by Liquid into window.sfpConfig before this script runs:
 *   window.sfpConfig = { enabled: true, variantId: '12345678901' }
 *
 * window.checkFreeItem is exposed globally so existing calls in
 * cart.js and build-box.js continue to work without modification.
 */

(function () {
  'use strict';

  // ── Always expose a global so cart.js / build-box.js never throw ──────────
  window.checkFreeItem = function () {};

  // ── Read config injected by Liquid ────────────────────────────────────────
  var cfg = window.sfpConfig;
  if (!cfg || !cfg.enabled || !cfg.variantId) return;

  var FREE_VARIANT_ID = String(cfg.variantId);

  // ── Concurrency guard ─────────────────────────────────────────────────────
  var _busy = false;

  // ── Empty-cart icon correction ────────────────────────────────────────────
  // window.updateCartCount (build-box.js) is called by renderContents with the
  // parsedState captured when only the subscription was removed (free product
  // still in cart, count=1).  Its internal await getCart() can resolve AFTER
  // removeFreeProduct already set the icon to 0, then overwrites it back to 1.
  // We store the correct empty-cart icon HTML here so the updateCartCount patch
  // can re-apply it.
  var _emptyCartIconHtml = null;

  // ── Debounce ──────────────────────────────────────────────────────────────
  // 150 ms is enough: queueSync() always fires AFTER the cart operation that
  // triggered it has already finished, so there is no interim-state risk.
  var _syncTimer = null;

  function queueSync() {
    clearTimeout(_syncTimer);
    _syncTimer = setTimeout(syncFreeProduct, 150);
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  function fetchCart() {
    return fetch('/cart.js').then(function (r) { return r.json(); });
  }

  // Update cart-drawer-items + totals panel + footer from a parsed section HTML string.
  function applyCartDrawerHTML(sectionHtml) {
    var doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    // cart-drawer-totals-main holds SUBTOTAL / SHIPPING / EST.TOTAL and lives in
    // the intermediate section outside cart-drawer-items — must be replaced
    // alongside cart-drawer-items so prices stay in sync.
    ['cart-drawer-items', '.cart-drawer-totals-main', '.cart-drawer__footer'].forEach(function (sel) {
      var t = document.querySelector(sel);
      var s = doc.querySelector(sel);
      if (t && s) t.replaceWith(s);
    });
    // Re-attach subscription dropdown listeners on the replaced elements.
    // Do NOT call window.sliderOn() here: .cart-add-on-products lives outside
    // cart-drawer-items in the DOM and is already Slick-initialized. Calling
    // .slick() on it again throws, which skips the cartSubItem() call inside
    // sliderOn() and leaves the new .cart-item-sub elements with no listeners.
    if (typeof cartSubItem === 'function') cartSubItem();
    moveFreeItemToBottom();
  }

  // Always keep the free item row at the very bottom of the cart items table,
  // regardless of how many products were added before or after it.
  function moveFreeItemToBottom() {
    var freeItem = document.querySelector('[free-item]');
    if (!freeItem) return;
    var tbody = freeItem.closest('tbody');
    if (tbody && tbody.lastElementChild !== freeItem) {
      tbody.appendChild(freeItem); // moves node to end without cloning
    }
  }

  // Fallback: fresh section fetch when the cart-add/update response has no
  // sections key (should be rare with Shopify's API).
  function refreshCartDrawer() {
    return fetch('/cart?section_id=cart-drawer')
      .then(function (r) { return r.text(); })
      .then(function (html) { applyCartDrawerHTML(html); });
  }

  // ── Add free product ──────────────────────────────────────────────────────
  // /cart/add.js increments qty if the item already exists, so always follow
  // with correctFreeProductQty() which sets qty to exactly 1 via /cart/update.js.

  function addFreeProduct() {
    return fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id:           FREE_VARIANT_ID,
        quantity:     1,
        sections_url: window.location.pathname,
      }),
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        // Always enforce qty=1. /cart/add.js increments if the item already
        // exists (e.g. stale _itemsHint triggered an extra add), so we must
        // idempotently set it back to 1 and update the DOM from the response.
        return correctFreeProductQty();
      })
      .catch(function (err) {
        console.error('[sfp] Failed to add free product:', err);
      });
  }

  // ── Correct free product qty ──────────────────────────────────────────────
  // Uses /cart/update.js (absolute set, not increment) to force gift qty to 1,
  // then updates both the cart drawer and the header icon bubble from the
  // server response so all counts stay in sync.

  function correctFreeProductQty() {
    var updates = {};
    updates[FREE_VARIANT_ID] = 1;
    return fetch('/cart/update.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates:      updates,
        sections:     ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    })
      .then(function (r) { return r.json(); })
      .then(function (response) {
        // Update cart icon bubble so the header count is always accurate.
        var iconHtml = response && response.sections && response.sections['cart-icon-bubble'];
        if (iconHtml) {
          var iconDoc = new DOMParser().parseFromString(iconHtml, 'text/html');
          var newIcon = iconDoc.querySelector('#shopify-section-cart-icon-bubble');
          var iconEl  = document.querySelector('a#cart-icon-bubble');
          if (newIcon && iconEl) iconEl.innerHTML = newIcon.innerHTML;
        }
        // Update cart drawer.
        var drawerHtml = response && response.sections && response.sections['cart-drawer'];
        if (drawerHtml) applyCartDrawerHTML(drawerHtml);
        else            return refreshCartDrawer();
      })
      .catch(function (err) {
        console.error('[sfp] Failed to correct free product qty:', err);
      });
  }

  // ── Remove free product ───────────────────────────────────────────────────
  // Same pattern: sections in the update request, update DOM directly from
  // the response (no extra getCart() call that window.updateCart would do).

  function removeFreeProduct() {
    var updates = {};
    updates[FREE_VARIANT_ID] = 0;

    return fetch('/cart/update.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates:      updates,
        sections:     ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    })
      .then(function (r) { return r.json(); })
      .then(function (response) {
        var html = response && response.sections && response.sections['cart-drawer'];
        if (!html) return refreshCartDrawer();

        var cartDrawerEl = document.querySelector('cart-drawer');
        if (!cartDrawerEl) { applyCartDrawerHTML(html); return; }

        // Full replacement of cart-drawer.innerHTML so intermediate elements
        // (add-ons, impact box) and the empty-cart state are rendered correctly.
        // This mirrors what build-box.js window.updateCart does at line 1179.
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newInner = doc.querySelector('cart-drawer');
        if (newInner) {
          cartDrawerEl.innerHTML = newInner.innerHTML;
          // Re-attach overlay close handler lost by innerHTML replacement.
          var overlay = cartDrawerEl.querySelector('#CartDrawer-Overlay');
          if (overlay) overlay.addEventListener('click', cartDrawerEl.close.bind(cartDrawerEl));
        }

        // Set / clear is-empty class based on actual cart state.
        cartDrawerEl.classList.toggle('is-empty', response.item_count === 0);

        // Update cart icon bubble count (the "1" badge in the header).
        var iconHtml = response.sections && response.sections['cart-icon-bubble'];
        if (iconHtml) {
          var iconDoc = new DOMParser().parseFromString(iconHtml, 'text/html');
          var newIcon = iconDoc.querySelector('#shopify-section-cart-icon-bubble');
          var iconEl  = document.querySelector('a#cart-icon-bubble');
          if (newIcon && iconEl) iconEl.innerHTML = newIcon.innerHTML;
          // If cart is now empty, save this HTML so our updateCartCount patch
          // can re-apply it if a concurrent stale call overwrites the icon.
          if (response.item_count === 0) {
            _emptyCartIconHtml = iconHtml;
            setTimeout(function () { _emptyCartIconHtml = null; }, 2000);
          }
        }

        // Re-init Slick and subscription listeners on the fresh elements.
        // Safe to call sliderOn() here because innerHTML replacement destroyed
        // the old .cart-add-on-products, so the new one is not yet Slick-initialized.
        if (typeof window.sliderOn === 'function') window.sliderOn();
        else if (typeof cartSubItem === 'function') cartSubItem();
      })
      .catch(function (err) {
        console.error('[sfp] Failed to remove free product:', err);
      });
  }

  // ── Sync: inspect cart AND DOM, then add / remove / refresh as needed ─────

  function syncFreeProduct() {
    if (_busy) {
      // Another sync is already in flight. Schedule a retry so this request
      // is not silently dropped (e.g. renderContents fires while removeFreeProduct
      // is still running — without retry the re-add never happens).
      clearTimeout(_syncTimer);
      _syncTimer = setTimeout(syncFreeProduct, 300);
      return;
    }
    _busy = true;

    fetchCart()
      .then(function (cart) {
        var hasSubscription = false;
        var hasFreeItem     = false;
        var freeItemQty     = 0;

        cart.items.forEach(function (item) {
          if (item.selling_plan_allocation)        hasSubscription = true;
          if (String(item.id) === FREE_VARIANT_ID) {
            hasFreeItem = true;
            freeItemQty = item.quantity;
          }
        });

        if (hasSubscription && !hasFreeItem)  return addFreeProduct();
        if (!hasSubscription && hasFreeItem)  return removeFreeProduct();

        if (hasSubscription && hasFreeItem) {
          // Reactive guard: correct qty if it somehow crept above 1.
          if (freeItemQty > 1)                      return correctFreeProductQty();
          // DOM race guard: cart has both items but the rendered drawer may
          // show a stale snapshot that omits the free product row.
          if (!document.querySelector('[free-item]')) return refreshCartDrawer();
        }
      })
      .catch(function (err) {
        console.error('[sfp] Sync error:', err);
      })
      .finally(function () {
        _busy = false;
        // Ensure free item is always last regardless of which branch ran
        // (add, remove no-op, or just a DOM refresh).
        moveFreeItemToBottom();
      });
  }

  // Force free product quantity back to exactly 1 if it has crept above that.
  // Also called unconditionally by addFreeProduct() to enforce qty=1 and to
  // update both the cart drawer AND the icon bubble in a single round-trip.
  function correctFreeProductQty() {
    var updates = {};
    updates[FREE_VARIANT_ID] = 1;
    return fetch('/cart/update.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates:      updates,
        sections:     ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    })
      .then(function (r) { return r.json(); })
      .then(function (response) {
        // Always update the cart icon bubble so the header count reflects the
        // correct total (including the newly added/corrected gift product).
        var iconHtml = response && response.sections && response.sections['cart-icon-bubble'];
        if (iconHtml) {
          var iconDoc = new DOMParser().parseFromString(iconHtml, 'text/html');
          var newIcon = iconDoc.querySelector('#shopify-section-cart-icon-bubble');
          var iconEl  = document.querySelector('a#cart-icon-bubble');
          if (newIcon && iconEl) iconEl.innerHTML = newIcon.innerHTML;
        }

        // Update cart drawer.
        var drawerHtml = response && response.sections && response.sections['cart-drawer'];
        if (drawerHtml) applyCartDrawerHTML(drawerHtml);
        else            return refreshCartDrawer();
      })
      .catch(function (err) {
        console.error('[sfp] Failed to correct free product qty:', err);
      });
  }

  // ── Public API ────────────────────────────────────────────────────────────
  window.checkFreeItem = queueSync;

  // ── Bootstrap ─────────────────────────────────────────────────────────────

  document.addEventListener('DOMContentLoaded', function () {
    var cartDrawerEl = document.querySelector('cart-drawer');

    // 1. Patch renderContents — catches product-form.js, addSubcription(), etc.
    if (cartDrawerEl && typeof cartDrawerEl.renderContents === 'function') {
      cartDrawerEl._origRenderContents = cartDrawerEl.renderContents;
      cartDrawerEl.renderContents = function () {
        cartDrawerEl._origRenderContents.apply(this, arguments);
        queueSync();
      };
    }

    // 2. Patch open() — catches any code that opens the drawer directly,
    //    including Skio's widget, without going through renderContents.
    //    open() is called AFTER the cart has been updated, so by the time
    //    the 150 ms timer fires the subscription is already in the cart.
    if (cartDrawerEl && typeof cartDrawerEl.open === 'function') {
      var _origOpen = cartDrawerEl.open;
      cartDrawerEl.open = async function () {
        await _origOpen.apply(this, arguments);
        queueSync();
      };
    }

    // 3. Patch window.updateCart — build-box.js and Skio may call this to
    //    refresh the drawer after an add.  We await completion so queueSync()
    //    fires AFTER updateCart has already written its (subscription-only)
    //    snapshot to the DOM, giving our sync a clean starting point.
    if (typeof window.updateCart === 'function') {
      var _origUpdateCart = window.updateCart;
      window.updateCart = async function () {
        var r = await _origUpdateCart.apply(this, arguments);
        queueSync();
        return r;
      };
    }

    // 4. PubSub — safety net for any cart change published via Dawn's event bus.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, queueSync);
    }

    // 5. Patch window.updateCartCount — corrects the stale-data race described
    //    above.  After the original runs (using potentially stale sections data),
    //    re-apply the saved empty-cart icon HTML if we just emptied the cart.
    if (typeof window.updateCartCount === 'function') {
      var _origUpdateCartCount = window.updateCartCount;
      window.updateCartCount = async function (data) {
        var r = await _origUpdateCartCount.apply(this, arguments);
        if (_emptyCartIconHtml) {
          var iconDoc2 = new DOMParser().parseFromString(_emptyCartIconHtml, 'text/html');
          var newIcon2 = iconDoc2.querySelector('#shopify-section-cart-icon-bubble');
          var iconEl2  = document.querySelector('a#cart-icon-bubble');
          if (newIcon2 && iconEl2) iconEl2.innerHTML = newIcon2.innerHTML;
        }
        return r;
      };
    }

    // 6. Initial check on page load.
    syncFreeProduct();
  });
})();
